import type { StatusCategory } from '../../../shared/types/config/status-builder.types';

const PROJECT_PHASE_CATEGORY_PREFIX = 'project-phase:';
const PROJECT_PHASE_COLORS = ['info', 'warning', 'secondary', 'success'] as const;

const normalizePhaseName = (name: string): string => name.trim();
const phaseKey = (name: string): string => normalizePhaseName(name).toLowerCase();

const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export function areStatusCategoriesEqual(
  left: StatusCategory[],
  right: StatusCategory[],
): boolean {
  return left.length === right.length && left.every((category, index) => {
    const other = right[index];
    return category.id === other.id
      && category.name === other.name
      && category.color === other.color
      && category.isBuiltIn === other.isBuiltIn
      && category.description === other.description
      && category.phaseKind === other.phaseKind
      && areStringArraysEqual(category.phaseNames, other.phaseNames)
      && category.attributes.length === other.attributes.length
      && category.attributes.every((attribute, attributeIndex) => {
        const otherAttribute = other.attributes[attributeIndex];
        return attribute.id === otherAttribute.id
          && attribute.pcfAttribute === otherAttribute.pcfAttribute
          && attribute.source === otherAttribute.source
          && attribute.value === otherAttribute.value;
      });
  });
}

const getProjectPhaseCategoryId = (phaseName: string): string =>
  `${PROJECT_PHASE_CATEGORY_PREFIX}${encodeURIComponent(phaseKey(phaseName))}`;

const isGeneratedProjectPhaseCategory = (category: StatusCategory): boolean =>
  category.id.startsWith(PROJECT_PHASE_CATEGORY_PREFIX);

const isUnconfiguredLegacyDefault = (category: StatusCategory): boolean =>
  category.isBuiltIn &&
  (category.id === 'existing' || category.id === 'demolition') &&
  category.attributes.length === 0;

export const removeGeneratedProjectPhaseStatusCategories = (
  categories: StatusCategory[],
): StatusCategory[] =>
  categories.filter((category) => !isGeneratedProjectPhaseCategory(category));

export const removeStaleProjectPhaseStatusCategories = (
  categories: StatusCategory[],
): StatusCategory[] =>
  removeGeneratedProjectPhaseStatusCategories(categories).filter(
    (category) =>
      !isUnconfiguredLegacyDefault(category),
  );

function normalizeProjectPhases(phaseNames: string[]): string[] {
  const seen = new Set<string>();
  return phaseNames.reduce<string[]>((result, rawName) => {
    const name = normalizePhaseName(rawName);
    const key = phaseKey(name);
    if (!name || seen.has(key)) {
      return result;
    }

    seen.add(key);
    result.push(name);
    return result;
  }, []);
}

function categoryMatchesPhase(category: StatusCategory, phaseName: string): boolean {
  const key = phaseKey(phaseName);
  return phaseKey(category.name) === key || category.phaseNames.some((name) => phaseKey(name) === key);
}

function createProjectPhaseCategory(phaseName: string, index: number): StatusCategory {
  return {
    id: getProjectPhaseCategoryId(phaseName),
    name: phaseName,
    color: PROJECT_PHASE_COLORS[index % PROJECT_PHASE_COLORS.length],
    isBuiltIn: true,
    description: `Elements created in the ${phaseName} project phase`,
    phaseKind: 'Created',
    phaseNames: [phaseName],
    attributes: [],
  };
}

export function reconcileProjectPhaseStatusCategories(
  currentCategories: StatusCategory[],
  rawPhaseNames: string[],
): StatusCategory[] {
  const phaseNames = normalizeProjectPhases(rawPhaseNames);
  if (phaseNames.length === 0) {
    return currentCategories;
  }

  const matchedIds = new Set<string>();
  const phaseCategories = phaseNames.map((phaseName, index) => {
    const generatedId = getProjectPhaseCategoryId(phaseName);
    const existingCategory = currentCategories.find(
      (category) =>
        !matchedIds.has(category.id) &&
        (category.id === generatedId || categoryMatchesPhase(category, phaseName)),
    );

    if (!existingCategory) {
      return createProjectPhaseCategory(phaseName, index);
    }

    matchedIds.add(existingCategory.id);
    return {
      ...existingCategory,
      name: phaseName,
      isBuiltIn: true,
      phaseNames:
        existingCategory.phaseKind === 'Demolished' && existingCategory.phaseNames.length === 0
          ? []
          : [phaseName],
    };
  });

  const preservedCustomCategories = removeStaleProjectPhaseStatusCategories(currentCategories).filter(
    (category) =>
      !matchedIds.has(category.id),
  );

  return [...phaseCategories, ...preservedCustomCategories];
}
